<script setup>

    /**
     * Vue Scripts
     */
    import {onMounted, ref} from "vue";
    import {objNotEmpty} from "@util/mixins";

    /**
     * Vue Components
     */
    import InputAutocomplete from "@component/InputAutocomplete/InputAutocomplete.vue";
    import {toNumber} from "lodash";

    const {
        apiKey,
        baseUri,
        basePath,
        conditions,
        conditionFormErr,
        content,
        labels,
        sex,
        title,
    } = ONCOLOGY;

    const {
        ageLabel,
        conditionLabel,
        locationLabel,
        searchButtonLabel,
        sexLabel,
    } = labels;

    const conditionSelected = ref(`${conditions[0].id}`)
    const ageSelected = ref('')
    const sexSelected = ref('')
    const locationSelected = ref('')
    const locationCoords = ref({lat: 0, lng: 0})

    const conditionErr = ref(false)


    onMounted(() => {
        setTimeout(() => {
            const selectButtonMutations = document.querySelectorAll('.custom-a11yselect-option');
            [...selectButtonMutations]
                .map(elm => elm.querySelector('button')
                    .addEventListener('click', () => bindSelectOption(elm)));
        }, 500);
    })

    /**
     * Updates the state with the name selected and the coords for it
     * @param {String} name Name of the location searched
     * @param {Object} coords Latitude and Longitude of the location selected
     */
    const getLocation = ({name, coords}) => {
        locationSelected.value = name
        locationCoords.value = coords
    }

    /**
     * Handler for the A11y Select dropdown to have the binding
     * of the click action from the select functionality
     * @param {HTMLElement} elm The "a11yselect" element
     */
    const bindSelectOption = (elm) => {
            conditionErr.value = elm?.dataset?.val === '0';
            conditionSelected.value = elm?.dataset?.val
    }

    const submitSelection = () => {
        conditionErr.value = false;

        if (conditionSelected.value === '0') {
            conditionErr.value = true;
            return '';
        }

        const query = new URLSearchParams();

        locationCoords.value.lat !== 0 && query.set('location.lat', locationCoords.value.lat);
        locationCoords.value.lng !== 0 && query.set('location.long', locationCoords.value.lng);
        sexSelected.value && query.set('sex', sexSelected.value);
        (ageSelected.value && !isNaN(toNumber(ageSelected.value))) && query.set('age', ageSelected.value.toString());

        window.location.assign(`${baseUri}/${conditionSelected.value}${basePath}?${query.toString()}`)
    }

</script>

<template>
    <form
        id="trial-search"
        class="predictive-search-form"
    >
        <div
            v-if="title || content"
            class="form-header">
            <h2
                v-if="title"
                v-html="title"
            />
            <p
                v-if="content"
                v-html="content"
            />
        </div>

        <div class="form-body">
            <div class="row">

                <div class="col-4 select-conditions">
                    <label
                        v-if="conditionLabel"
                        for="conditions"
                        :aria-label="conditionLabel"
                        v-html="conditionLabel"
                    />
                    <select
                        v-if="conditions.length"

                        id="conditions"
                        name="option"
                        required
                        aria-required="true"
                        :aria-invalid="conditionErr"
                        :aria-errormessage="conditionFormErr"
                    >
                        <option
                            v-for="({antidoteId, id, name}) in conditions"
                            :key="id"
                            :value="antidoteId"
                            :selected="id === 0"
                            v-html="name"
                        />
                    </select>
                    <p
                        v-if="conditionErr && conditionSelected === '0'"
                        class="form-error"
                        v-html="conditionFormErr"
                    />
                </div>

                <div class="col-3 select-location">
                    <InputAutocomplete
                        id="google-oncology-location-search"

                        :apiKey="apiKey"
                        :label="locationLabel"
                        :placeholder="locationLabel"
                        select-on-enter
                        :value="locationSelected"

                        @getLatLng="getLocation"
                    />
                </div>

                <div class="col-1 select-age">
                    <label
                        v-if="ageLabel"
                        for="age"
                        :aria-label="ageLabel"
                        v-html="ageLabel"
                    />

                    <input
                        v-model="ageSelected"
                        id="age"
                        name="age"
                        type="text"
                        required
                        aria-required="true"
                        inputmode="numeric"
                        pattern="[0-9]*"
                    />

                </div>

                <div class="col-2 select-sex">
                    <h4
                        v-if="sexLabel"
                        :aria-label="sexLabel"
                        v-html="sexLabel"
                    />

                    <div v-if="objNotEmpty(sex)"
                         v-for="({id, name, slug}) in sex"
                         :key="id"
                         class="radio-box">
                        <input
                            v-model="sexSelected"

                            name="sex"
                            required
                            aria-required="true"
                            type="radio"

                            :id="slug"
                            :value="slug"
                        />
                        <label
                            for="slug"
                            v-html="name"
                        />
                    </div>
                </div>

                <div class="col-2 submit-form">
                    <button
                        v-if="searchButtonLabel"

                        :disabled="conditionSelected === '0'"
                        :aria-disabled="conditionSelected === '0'"

                        class="btn-info"
                        :class="{'btn-disabled': conditionSelected === '0'}"
                        type="submit"

                        v-html="searchButtonLabel"

                        @keydown.enter.prevent="submitSelection"
                        @click.prevent="submitSelection"
                    />
                </div>

            </div>
        </div>

    </form>
</template>

<script>
    export default {
        name: "Antidote"
    }
</script>

<style scoped lang="scss">

    @import '../../../styles/common/variables';

    .btn-disabled {
        opacity: 0.5;
        cursor: not-allowed;
        &:hover {
            color: $blue;
            background-color:$light-teal;
            border:0.0625rem solid $light-teal;
            text-decoration:none;
        }
    }
</style>
